"use client";
import { use, useMemo } from "react";

import { Skeleton } from "@offstreetparking/design-system/feedback";
import { CreatePermitWizard } from "@offstreetparking/portal-flow/components/CreatePermitWizard/CreatePermitWizard";
import { PermitStoreProvider } from "@offstreetparking/portal-flow/components/CreatePermitWizard/PermitContextProvider";
import { PermitTypeUnavailable } from "@offstreetparking/portal-flow/components/PermitTypeUnavailable";
import { ParkingDetailsSkeleton } from "@offstreetparking/portal-flow/components/dataDisplay/parkingDetails";
import { NotFoundAlert } from "@offstreetparking/portal-flow/components/forms/Alert/NotFoundAlert";
import { ServerErrorAlert } from "@offstreetparking/portal-flow/components/forms/Alert/ServerErrorAlert";
import { ContactSkeleton } from "@offstreetparking/portal-flow/components/layouts/Contact";
import { ContentCard } from "@offstreetparking/portal-flow/components/layouts/ContentCard";
import { PermitFormLayout } from "@offstreetparking/portal-flow/components/layouts/PermitFormLayout";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { useParker } from "~/components/providers/AuthProvider";
import { offstreetClient } from "~/services/offstreetApi";

export default function EventPage(props: { params: Promise<{ locale: string; locationLookupId: string }> }) {
	const params = use(props.params);
	const { locationLookupId } = params;
	const { data, isPending, error } = offstreetClient.locationLookup.getLocationLookupEntitiesByIdentifier.useQuery({
		id: locationLookupId,
	});
	const { isInitialLoading: isParkerInitialLoading, parker } = useParker();
	const permitType = data?.permitType;
	const instructions = data?.permitTypeInstructions?.registrationInstructions ?? undefined;
	const hasOverrideNotices = (data?.overrideNotices?.length ?? 0) > 0;
	const formData = useMemo(() => {
		if (!parker) {
			return undefined;
		}
		return {
			vehicles: parker.defaultVehicle ? [parker.defaultVehicle] : [],
			email: parker.email ?? undefined,
		};
	}, [parker]);

	if (isPending || isParkerInitialLoading) {
		return (
			<CommonLayout
				infoSlot={
					<div className="space-y-12">
						<div className="space-y-4">
							<Skeleton height={60} width={450} />
							<Skeleton height={60} width={225} />
						</div>

						<ContactSkeleton />
					</div>
				}
				isLoading={true}
			>
				<ContentCard>
					<ParkingDetailsSkeleton />
					<div className="border-neutral-faded border-t p-8 xl:hidden">
						<ContactSkeleton />
					</div>
				</ContentCard>
			</CommonLayout>
		);
	}

	if (error) {
		return (
			<CommonLayout>
				<ServerErrorAlert serverError={error} />
			</CommonLayout>
		);
	}

	if (!permitType) {
		return (
			<CommonLayout>
				<NotFoundAlert />
			</CommonLayout>
		);
	}

	return (
		<PermitStoreProvider
			initialValues={{
				formData,
				wizardSettings: permitType.wizardSettings,
				isParkingTimeStepForced: hasOverrideNotices,
			}}
		>
			<PermitFormLayout
				name={permitType.name}
				branding={permitType.branding}
				companyName={permitType.company.name}
				whiteLabel={permitType.whiteLabel}
				support={permitType.company.support}
				contact={permitType.contact}
				eventId={locationLookupId}
				brandingUrl={permitType.brandingUrl}
				venue={permitType.venue}
				registrationInstructions={instructions}
			>
				{permitType.status !== "active" ? (
					<PermitTypeUnavailable status={permitType.status} choices={permitType.wizardSettings.choices} />
				) : (
					<CreatePermitWizard
						permitType={permitType}
						overrideNotices={data?.overrideNotices}
						entryLookupId={locationLookupId}
						tenantId={data?.tenantId}
						registrationInstructions={instructions}
					/>
				)}
			</PermitFormLayout>
		</PermitStoreProvider>
	);
}
